import { Directive, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { getIsOnSystemPage, getSelectedAccountName } from 'src/app/store/select-account/select-account.selector';
import { RegisterOptions } from './register-options';

@Directive()
export abstract class RegisterView<TRegisterOptions extends RegisterOptions, TOrderingOptions extends OrderingOption> {
   @Input() callState: CallState;

   @Input() columns: string[];

   @Input() orderingOptions: TOrderingOptions[];

   @Input() hasNextPage: boolean;

   @Input() searchText: string | null;

   @Input() name: string;

   @Input() hideHeader = false;

   @Input() showCreate = false;

   @Input() showAllUsersControl = false;

   @Input() showAllAccountsControl = false;

   @Input() includeInactivePromptsControl = false;

   @Input() totalCount: number | null;

   @Input() isLoading: boolean;

   @Input() pagingCallState: CallState;

   @Output() orderingOptionsChange = new EventEmitter<TOrderingOptions[]>();

   @Input() pluralNameOverride: string | null;

   @Output() create = new EventEmitter<void>();

   @Output() quickFilterChange = new EventEmitter<TRegisterOptions>();

   @Output() loadMore = new EventEmitter<void>();

   selectedAccount$: Observable<string | null>;

   isOnSystemPage$: Observable<boolean | undefined>;

   isSystemUser$: Observable<boolean>;

   registerOptions: TRegisterOptions;

   pluralName: string;

   constructor(public store: Store) {
      this.selectedAccount$ = this.store.pipe(select(getSelectedAccountName));
      this.isOnSystemPage$ = this.store.pipe(select(getIsOnSystemPage));
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
   }

   onCreate(): void {
      this.create.emit();
   }

   onQuickFilterChange(quickFilter: TRegisterOptions): void {
      this.registerOptions = { ...this.registerOptions, ...quickFilter };
      this.quickFilterChange.emit(this.registerOptions);
   }

   onOrderingOptionsChange(orderingOptions: TOrderingOptions[]): void {
      this.orderingOptionsChange.emit(orderingOptions);
   }

   onLoadMore(): void {
      this.loadMore.emit();
   }

   getSort(column: string): string {
      const option = this.orderingOptions?.find((option) => option.field === column);
      if (option) {
         if (option.direction === 'Desc') {
            return 'arrow_downward';
         } else if (option.direction === 'Asc') {
            return 'arrow_upward';
         } else {
            return '';
         }
      }
      return '';
   }

   getOrderingOption(orderingOptions: TOrderingOptions[], field: string): string {
      const foundOption = orderingOptions.find((option) => option.field === field);
      const returnValue = foundOption?.direction?.toString();

      if (returnValue) return returnValue;
      return OrderingDirections.NotSet;
   }
}

@Directive()
export abstract class EntityRegisterView<
      TItem,
      TRegisterOptions extends RegisterOptions,
      TOrderingOptions extends OrderingOption
   >
   extends RegisterView<TRegisterOptions, TOrderingOptions>
   implements OnInit
{
   @Input() dataSource: TItem[];

   abstract getRouterLink(id: number): string;

   protected abstract selectQuickFilters(): (state: object) => TRegisterOptions | null;

   protected onQuickFiltersInit(quickFilters: TRegisterOptions): void {
      this.registerOptions = quickFilters;
   }

   ngOnInit(): void {
      this.store.pipe(select(this.selectQuickFilters()), take(1)).subscribe((quickFilters) => {
         if (quickFilters) {
            this.searchText = quickFilters.searchText ?? '';
            this.onQuickFiltersInit(quickFilters);
         }
      });
   }
}

@Directive()
export abstract class EntityTable<
   TItem,
   TRegisterOptions extends RegisterOptions,
   TOrderingOptions extends OrderingOption
> extends RegisterView<TRegisterOptions, TOrderingOptions> {
   @Input() dataSource: TItem[];

   abstract onRowClick(entity: TItem): void;
}
